import { useEffect, useState } from 'react'
import { toast } from 'react-toastify'

import { useGetCompanyConfigFileRawQuery } from '../../app/apiSlice'
import { useUpdateTrackingPolicyMutation } from '../../app/apiSliceConfigStudioApi'
import { AdminPage, AdminPageHeader, Loader } from '../../common/components'
import { ActionModal, Breadcrumbs, Divider } from '../../common/designs'
import { useEnhancedNavigate } from '../../common/hooks'
import TrackingPolicySection from './TrackingPolicySection'
import type {
  TrackingPolicy,
  TrackingPolicyRule,
  UpdateTrackingPolicyPayload,
} from './types'

const EMPTY_POLICY: TrackingPolicy = { whitelist: [], blacklist: [] }

const GlobalDiscoveryRulesPage = () => {
  const { enhancedNavigate: navigate } = useEnhancedNavigate()
  const { data: configFileRaw, isLoading } = useGetCompanyConfigFileRawQuery()
  const [updateTrackingPolicy, { isLoading: isSaving }] = useUpdateTrackingPolicyMutation()
  const [trackingPolicy, setTrackingPolicy] = useState<TrackingPolicy>(EMPTY_POLICY)
  const [ruleToDelete, setRuleToDelete] = useState<{
    listType: keyof TrackingPolicy
    index: number
  } | null>(null)

  useEffect(() => {
    const policy = configFileRaw?.tracking_policy as Partial<TrackingPolicy> | undefined
    setTrackingPolicy({
      whitelist: policy?.whitelist ?? [],
      blacklist: policy?.blacklist ?? [],
    })
  }, [configFileRaw])

  const saveTrackingPolicy = async (nextPolicy: TrackingPolicy) => {
    const payload: UpdateTrackingPolicyPayload = {
      team_id: null,
      tracking_policy: nextPolicy,
    }
    const response = await updateTrackingPolicy(payload)
    if ('error' in response) {
      toast.error('Unable to save tracking policy.')
      return false
    }
    setTrackingPolicy(nextPolicy)
    return true
  }

  const addRule = async (listType: keyof TrackingPolicy, rule: TrackingPolicyRule) => {
    await saveTrackingPolicy({
      ...trackingPolicy,
      [listType]: [...trackingPolicy[listType], rule],
    })
  }

  const updateRule = async (
    listType: keyof TrackingPolicy,
    index: number,
    rule: TrackingPolicyRule,
  ) => {
    const rules = [...trackingPolicy[listType]]
    rules[index] = rule
    await saveTrackingPolicy({ ...trackingPolicy, [listType]: rules })
  }

  const deleteRule = async () => {
    if (!ruleToDelete) return
    const { listType, index } = ruleToDelete
    const rules = trackingPolicy[listType].filter((_, ruleIndex) => ruleIndex !== index)
    const saved = await saveTrackingPolicy({ ...trackingPolicy, [listType]: rules })
    if (saved) setRuleToDelete(null)
  }

  if (isLoading) return <Loader />

  return (
    <AdminPage width='full'>
      <Breadcrumbs
        variant='secondary'
        items={[
          { label: 'Configure Data Collection', onClick: () => navigate('..') },
          { label: 'Global Discovery Rules' },
        ]}
      />
      <Divider className='my-4' color='lightGray' />
      <AdminPageHeader
        title='Global Discovery Rules'
        description='Control which events are kept or dropped across all applications.'
      />
      <div className='mt-6 space-y-6'>
        <TrackingPolicySection
          title='Allowlist'
          description='If any allow rule exists, only matching events are kept.'
          dotColorClass='bg-blue-600'
          rules={trackingPolicy.whitelist}
          isSaving={isSaving}
          onAddRule={(rule) => addRule('whitelist', rule)}
          onUpdateRule={(index, rule) => updateRule('whitelist', index, rule)}
          onRequestDeleteRule={(index) => setRuleToDelete({ listType: 'whitelist', index })}
        />
        <TrackingPolicySection
          title='Blocklist'
          description='Matching events are always dropped. Blocklist wins over allowlist.'
          dotColorClass='bg-red-500'
          rules={trackingPolicy.blacklist}
          isSaving={isSaving}
          onAddRule={(rule) => addRule('blacklist', rule)}
          onUpdateRule={(index, rule) => updateRule('blacklist', index, rule)}
          onRequestDeleteRule={(index) => setRuleToDelete({ listType: 'blacklist', index })}
        />
      </div>
      <ActionModal
        variant='destructive'
        label='Are you sure you want to delete this rule?'
        description='This action will remove the rule from the tracking policy.'
        actionLabel='Delete'
        open={Boolean(ruleToDelete)}
        loading={isSaving}
        onClose={() => setRuleToDelete(null)}
        onAction={deleteRule}
      />
    </AdminPage>
  )
}

export default GlobalDiscoveryRulesPage
